import { useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { 
  Phone, 
  Mail, 
  MapPin, 
  Award, 
  Users, 
  Star, 
  CheckCircle,
  Shield,
  Heart,
  Calendar,
  BookOpen,
  Home,
  GraduationCap,
  Trophy,
  Building2,
  ClipboardList,
  Target,
  Clock,
  DollarSign,
  Settings,
  MessageCircle,
  UserCheck
} from 'lucide-react';

const MichelleJessen = () => {
  // SEO meta tags setup
  useEffect(() => {
    
    const metaTags = [
      { name: 'description', content: 'Meet Michelle Jessen, Majority Owner and Office Manager of Colorado First Plumbing. Expert in payroll, CRM, scheduling, customer care, and QuickBooks. Making Colorado First Plumbing woman-owned.' },
      { name: 'keywords', content: 'Michelle Jessen Colorado First Plumbing, woman owned business Colorado, office management expert, customer service specialist, QuickBooks expert, CRM specialist Denver' },
      { property: 'og:title', content: 'Michelle Jessen - Office Management & Majority Owner | Colorado First Plumbing' },
      { property: 'og:description', content: 'Majority Owner making Colorado First Plumbing woman-owned. Expert in office management, customer care, and business operations.' },
      { property: 'og:type', content: 'profile' },
      { property: 'og:url', content: 'https://colorado-plumbing.com/team/michelle-jessen' },
    ];
    
    metaTags.forEach(tag => {
      let metaElement = document.querySelector(`meta[${tag.name ? 'name' : 'property'}="${tag.name || tag.property}"]`);
      if (metaElement) {
        metaElement.setAttribute('content', tag.content);
      } else {
        metaElement = document.createElement('meta');
        if (tag.name) {
          metaElement.setAttribute('name', tag.name);
        } else {
          metaElement.setAttribute('property', tag.property);
        }
        metaElement.setAttribute('content', tag.content);
        document.head.appendChild(metaElement);
      }
    });

    // Structured data for Michelle Jessen
    const structuredData = {
      '@context': 'https://schema.org',
      '@type': 'Person',
      'name': 'Michelle Jessen',
      'jobTitle': 'Office Management & Majority Owner',
      'worksFor': {
        '@type': 'LocalBusiness',
        'name': 'Colorado First Plumbing and Sewer LLC',
        'url': 'https://colorado-plumbing.com'
      },
      'description': 'Majority Owner making Colorado First Plumbing woman-owned. Expert in office management, payroll, CRM, scheduling, and customer care.',
      'knowsAbout': ['Office Management', 'Customer Service', 'CRM Systems', 'QuickBooks', 'Payroll Management', 'Business Operations'],
      'homeLocation': 'Westminster, Colorado',
      'url': 'https://colorado-plumbing.com/team/michelle-jessen'
    };

    let structuredDataScript = document.querySelector('#michelle-structured-data');
    if (structuredDataScript) {
      structuredDataScript.textContent = JSON.stringify(structuredData);
    } else {
      structuredDataScript = document.createElement('script');
      structuredDataScript.setAttribute('id', 'michelle-structured-data');
      structuredDataScript.setAttribute('type', 'application/ld+json');
      structuredDataScript.textContent = JSON.stringify(structuredData);
      document.head.appendChild(structuredDataScript);
    }

    return () => {
      const script = document.querySelector('#michelle-structured-data');
      if (script) {
        document.head.removeChild(script);
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-light to-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-60 pb-16 bg-gradient-to-br from-success-primary to-success-eco text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-success-primary/20 to-trust-primary/20"></div>
        <div className="container-custom relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center mb-6">
                <Users className="w-8 h-8 text-white mr-3" />
                <span className="px-4 py-2 bg-white/20 rounded-full text-white font-medium text-sm border border-white/30">
                  Woman-Owned Business
                </span>
              </div>
              
              <h1 className="text-4xl md:text-5xl font-bold mb-4 leading-tight">
                Michelle Jessen
              </h1>
              <h2 className="text-2xl md:text-3xl text-white/90 font-semibold mb-6">
                Office Management & Majority Owner
              </h2>
              
              <p className="text-xl text-white/90 mb-8 leading-relaxed">
                Majority Owner making Colorado First Plumbing and Sewer LLC an officially woman-owned business. 
                Expert in office management, customer care, and business operations with a focus on exceptional service.
              </p>
              
              {/* Key Highlights */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                <div className="bg-white/10 backdrop-blur-md rounded-lg p-4 border border-white/20">
                  <div className="text-lg font-bold text-white">Majority Owner</div>
                  <div className="text-sm text-white/80">Woman-Owned Business</div>
                </div>
                <div className="bg-white/10 backdrop-blur-md rounded-lg p-4 border border-white/20">
                  <div className="text-lg font-bold text-white">Colorado Native</div>
                  <div className="text-sm text-white/80">Westminster Born</div>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                
                <a 
                  href="#expertise" 
                  className="inline-flex items-center px-6 py-3 rounded-full bg-white/10 backdrop-blur-md text-white hover:bg-white/20 transition-all duration-300 font-medium border border-white/30"
                >
                  <BookOpen size={16} className="mr-2" />
                  View Expertise
                </a>
              </div>
            </div>
            
            {/* Team Photos */}
            <div className="grid grid-cols-1 gap-4">
              <div className="aspect-[4/3] rounded-xl overflow-hidden shadow-2xl border-4 border-white/30">
                <img 
                  src="/colorado-plumbing-uploads/team/womanownedplumbing.png" 
                  alt="Michelle Jessen - Woman-Owned Business Owner at Colorado First Plumbing" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="aspect-[4/3] rounded-xl overflow-hidden shadow-2xl border-4 border-white/30">
                <img 
                  src="/colorado-plumbing-uploads/team/michellejessen.png" 
                  alt="Michelle Jessen - Office Management & Customer Care" 
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Business Leadership Section */}
      <section id="expertise" className="py-16 bg-white">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-authority-navy mb-4">
              Business Leadership & Expertise
            </h2>
            <p className="text-xl text-neutral-gray max-w-3xl mx-auto">
              Leading Colorado First Plumbing's operations with expert office management, customer care, and business development expertise.
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Leadership Role */}
            <div className="bg-success-light rounded-xl p-8 border border-success-primary/20">
              <div className="flex items-center mb-6">
                <Trophy className="w-8 h-8 text-success-primary mr-3" />
                <div>
                  <h3 className="text-xl font-bold text-authority-navy">Majority Owner & Leader</h3>
                  <p className="text-success-primary font-medium">Colorado First Plumbing and Sewer LLC</p>
                </div>
              </div>
              
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-success-primary mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-neutral-gray">Majority ownership making the business woman-owned</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-success-primary mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-neutral-gray">Strategic business planning and growth management</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-success-primary mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-neutral-gray">Family business operations with husband Codee Jessen</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-success-primary mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-neutral-gray">Top performer driving company success and growth</span>
                </li>
              </ul>
            </div>

            {/* Core Responsibilities */}
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-trust-light to-white rounded-xl p-6 border border-trust-primary/20 shadow-md">
                <div className="flex items-center mb-4">
                  <MessageCircle className="w-6 h-6 text-trust-primary mr-3" />
                  <h4 className="text-lg font-bold text-authority-navy">Customer Care Excellence</h4>
                </div>
                <p className="text-neutral-gray text-sm mb-3">
                  Leading customer service operations to ensure exceptional experiences for every client.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-trust-primary rounded-full mr-3 mt-2 flex-shrink-0"></span>
                    <span className="text-sm text-neutral-gray">Customer relationship management</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-trust-primary rounded-full mr-3 mt-2 flex-shrink-0"></span>
                    <span className="text-sm text-neutral-gray">Service quality assurance</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-action-primary/10 to-white rounded-xl p-6 border border-action-primary/20 shadow-md">
                <div className="flex items-center mb-4">
                  <Settings className="w-6 h-6 text-action-primary mr-3" />
                  <h4 className="text-lg font-bold text-authority-navy">Business Operations</h4>
                </div>
                <p className="text-neutral-gray text-sm mb-3">
                  Comprehensive management of daily operations and business systems.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-action-primary rounded-full mr-3 mt-2 flex-shrink-0"></span>
                    <span className="text-sm text-neutral-gray">Scheduling and coordination</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-action-primary rounded-full mr-3 mt-2 flex-shrink-0"></span>
                    <span className="text-sm text-neutral-gray">Administrative oversight</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technical Skills Section */}
      <section className="py-16 bg-gradient-to-br from-neutral-light to-white">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-authority-navy mb-4">
              Office Management & Technical Skills
            </h2>
            <p className="text-xl text-neutral-gray max-w-2xl mx-auto">
              Expert-level proficiency in business systems, customer management, and operational excellence.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl p-6 shadow-lg border border-success-primary/20 hover:shadow-xl transition-all duration-300">
              <div className="flex items-center mb-4">
                <DollarSign className="w-8 h-8 text-success-primary mr-3" />
                <h3 className="text-lg font-bold text-authority-navy">Payroll Management</h3>
              </div>
              <p className="text-neutral-gray mb-4">
                Expert payroll processing and employee compensation management ensuring accurate, timely payments.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center text-sm text-neutral-gray">
                  <CheckCircle className="w-4 h-4 text-success-primary mr-2" />
                  Multi-state payroll processing
                </li>
                <li className="flex items-center text-sm text-neutral-gray">
                  <CheckCircle className="w-4 h-4 text-success-primary mr-2" />
                  Tax compliance management
                </li>
                <li className="flex items-center text-sm text-neutral-gray">
                  <CheckCircle className="w-4 h-4 text-success-primary mr-2" />
                  Benefits administration
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg border border-trust-primary/20 hover:shadow-xl transition-all duration-300">
              <div className="flex items-center mb-4">
                <UserCheck className="w-8 h-8 text-trust-primary mr-3" />
                <h3 className="text-lg font-bold text-authority-navy">CRM Systems</h3>
              </div>
              <p className="text-neutral-gray mb-4">
                Advanced customer relationship management ensuring personalized service and customer retention.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center text-sm text-neutral-gray">
                  <CheckCircle className="w-4 h-4 text-success-primary mr-2" />
                  Customer database management
                </li>
                <li className="flex items-center text-sm text-neutral-gray">
                  <CheckCircle className="w-4 h-4 text-success-primary mr-2" />
                  Service history tracking
                </li>
                <li className="flex items-center text-sm text-neutral-gray">
                  <CheckCircle className="w-4 h-4 text-success-primary mr-2" />
                  Follow-up automation
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg border border-action-primary/20 hover:shadow-xl transition-all duration-300">
              <div className="flex items-center mb-4">
                <Calendar className="w-8 h-8 text-action-primary mr-3" />
                <h3 className="text-lg font-bold text-authority-navy">Scheduling Systems</h3>
              </div>
              <p className="text-neutral-gray mb-4">
                Advanced scheduling coordination optimizing technician routes and customer convenience.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center text-sm text-neutral-gray">
                  <CheckCircle className="w-4 h-4 text-success-primary mr-2" />
                  Route optimization
                </li>
                <li className="flex items-center text-sm text-neutral-gray">
                  <CheckCircle className="w-4 h-4 text-success-primary mr-2" />
                  Emergency scheduling
                </li>
                <li className="flex items-center text-sm text-neutral-gray">
                  <CheckCircle className="w-4 h-4 text-success-primary mr-2" />
                  Customer notifications
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg border border-authority-navy/20 hover:shadow-xl transition-all duration-300">
              <div className="flex items-center mb-4">
                <ClipboardList className="w-8 h-8 text-authority-navy mr-3" />
                <h3 className="text-lg font-bold text-authority-navy">QuickBooks Expert</h3>
              </div>
              <p className="text-neutral-gray mb-4">
                Comprehensive financial management using QuickBooks for accurate business accounting.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center text-sm text-neutral-gray">
                  <CheckCircle className="w-4 h-4 text-success-primary mr-2" />
                  Financial reporting
                </li>
                <li className="flex items-center text-sm text-neutral-gray">
                  <CheckCircle className="w-4 h-4 text-success-primary mr-2" />
                  Invoice management
                </li>
                <li className="flex items-center text-sm text-neutral-gray">
                  <CheckCircle className="w-4 h-4 text-success-primary mr-2" />
                  Tax preparation support
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg border border-success-eco/20 hover:shadow-xl transition-all duration-300">
              <div className="flex items-center mb-4">
                <MessageCircle className="w-8 h-8 text-success-eco mr-3" />
                <h3 className="text-lg font-bold text-authority-navy">Customer Care</h3>
              </div>
              <p className="text-neutral-gray mb-4">
                Exceptional customer service delivery ensuring positive experiences and customer loyalty.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center text-sm text-neutral-gray">
                  <CheckCircle className="w-4 h-4 text-success-primary mr-2" />
                  Issue resolution
                </li>
                <li className="flex items-center text-sm text-neutral-gray">
                  <CheckCircle className="w-4 h-4 text-success-primary mr-2" />
                  Quality assurance
                </li>
                <li className="flex items-center text-sm text-neutral-gray">
                  <CheckCircle className="w-4 h-4 text-success-primary mr-2" />
                  Feedback management
                </li>
              </ul>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg border border-action-warm/20 hover:shadow-xl transition-all duration-300">
              <div className="flex items-center mb-4">
                <Target className="w-8 h-8 text-action-warm mr-3" />
                <h3 className="text-lg font-bold text-authority-navy">Performance Management</h3>
              </div>
              <p className="text-neutral-gray mb-4">
                Strategic performance tracking and business optimization for continuous improvement.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center text-sm text-neutral-gray">
                  <CheckCircle className="w-4 h-4 text-success-primary mr-2" />
                  KPI tracking
                </li>
                <li className="flex items-center text-sm text-neutral-gray">
                  <CheckCircle className="w-4 h-4 text-success-primary mr-2" />
                  Process improvement
                </li>
                <li className="flex items-center text-sm text-neutral-gray">
                  <CheckCircle className="w-4 h-4 text-success-primary mr-2" />
                  Team coordination
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Personal & Community Section */}
      <section className="py-16 bg-white">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-authority-navy mb-4">
              Personal Life & Community Involvement
            </h2>
            <p className="text-xl text-neutral-gray max-w-2xl mx-auto">
              Colorado native with deep community roots and commitment to family and educational support.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-gradient-to-br from-success-light to-white rounded-xl p-8 border border-success-primary/20">
              <div className="flex items-center mb-6">
                <Heart className="w-8 h-8 text-success-primary mr-3" />
                <h3 className="text-xl font-bold text-authority-navy">Family Life</h3>
              </div>
              
              <ul className="space-y-4">
                <li className="flex items-start">
                  <Users className="w-5 h-5 text-success-primary mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-authority-navy">Loving Mother</div>
                    <div className="text-neutral-gray">Mother to Christian and Melodee</div>
                  </div>
                </li>
                <li className="flex items-start">
                  <Heart className="w-5 h-5 text-success-primary mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-authority-navy">Loving Wife</div>
                    <div className="text-neutral-gray">Married to Codee Jessen (Master Plumber & Co-owner)</div>
                  </div>
                </li>
                <li className="flex items-start">
                  <MapPin className="w-5 h-5 text-success-primary mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-authority-navy">Colorado Native</div>
                    <div className="text-neutral-gray">Born and raised in Westminster, Colorado</div>
                  </div>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-trust-light to-white rounded-xl p-8 border border-trust-primary/20">
              <div className="flex items-center mb-6">
                <GraduationCap className="w-8 h-8 text-trust-primary mr-3" />
                <h3 className="text-xl font-bold text-authority-navy">Community Service</h3>
              </div>
              
              <ul className="space-y-4">
                <li className="flex items-start">
                  <BookOpen className="w-5 h-5 text-trust-primary mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-authority-navy">School Volunteer</div>
                    <div className="text-neutral-gray">Regular volunteer at Highlands Ranch Elementary schools</div>
                  </div>
                </li>
                <li className="flex items-start">
                  <Users className="w-5 h-5 text-trust-primary mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-authority-navy">Educational Support</div>
                    <div className="text-neutral-gray">Active in supporting local educational initiatives</div>
                  </div>
                </li>
                <li className="flex items-start">
                  <Shield className="w-5 h-5 text-trust-primary mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-authority-navy">Community Leader</div>
                    <div className="text-neutral-gray">Committed to strengthening Highlands Ranch community</div>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Michelle Section */}
      <section className="py-16 bg-gradient-to-br from-success-primary to-success-eco text-white">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Experience Woman-Owned Business Excellence
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Work with Michelle and the Colorado First Plumbing team for exceptional customer service and professional plumbing solutions.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-2xl mx-auto">
            
            
          </div>
          
          {/* Woman-Owned Business Badge */}
          <div className="mt-8 p-4 bg-white/10 rounded-xl border border-white/30 max-w-lg mx-auto">
            <div className="flex items-center justify-center mb-2">
              <Trophy className="w-5 h-5 text-white mr-2" />
              <span className="text-white font-semibold">Certified Woman-Owned Business</span>
            </div>
            <p className="text-sm text-white/90">
              Supporting local women in business and community leadership.
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MichelleJessen;